/**
 * WP-582 B2 (FEAT-1404) — the orient "rituals surface": direction/verdict-ask/spine one-liners,
 * server-computed (convex/agentKnowledge/orientRituals.ts) and rendered identically here and on
 * the pb CLI (TEN-2278: this connector cannot import convex/, so the payload is pre-rendered
 * strings — same pattern as `buildTaskAlignmentLines`, orient-shared.ts). New file rather than an
 * addition to orient-shared.ts (already at the STD-2/DEC-1504 500-LOC budget).
 *
 * `buildAlreadyOrientedResult` (added PR #532 round 1, T15) also lives here rather than inline in
 * orient.ts — that file is grandfathered at its own 500-LOC budget (STD-2/DEC-1504), so this is
 * the offsetting extraction for the round's fixes there.
 */
import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { success, type ToolResult } from "../envelope.js";

export interface RitualsSurfacePayload {
  directionLine?: string | null;
  verdictAskLine?: string;
  spineLine?: string | null;
  /** D1 residue fix (named item c): data-driven, no fallback — absent/null means nothing to
   *  disclose (no C1 data yet, or nothing has ever been dropped), never a blank line. */
  atCapLine?: string | null;
}

/**
 * `directionLine`/`spineLine` always render one line each — falling back to an explicit
 * "not computed yet" line rather than being silently absent when the server serves `null` (the
 * no-row arm doneWhen names). `verdictAskLine` renders ONLY when the server sent a non-empty
 * string: it has no no-row arm of its own (zero live asks is a well-defined state), so an
 * empty/absent value means "nothing to ask" — correctly nothing, not a fallback line.
 *
 * WP-582 B2 round 1 (Codex P2 review, PR #532, T12): `fetchFailed` distinguishes "the
 * chain.getOrientView call itself threw and orient.ts swallowed it" from a genuine no-row arm —
 * without it both render the identical "not computed yet" text and an agent cannot tell an
 * outage from a workspace that has simply never run `pb direction refresh`.
 */
export function buildRitualsSurfaceLines(
  view: RitualsSurfacePayload | null | undefined,
  opts?: { fetchFailed?: boolean },
): string[] {
  const lines: string[] = [""];
  if (opts?.fetchFailed) {
    lines.push("Direction: unavailable — orient fetch failed, retry `orient`");
    lines.push("Spine check: unavailable — orient fetch failed, retry `orient`");
    return lines;
  }
  lines.push(view?.directionLine ?? "Direction: not computed yet — `pb direction refresh`");
  if (view?.verdictAskLine) lines.push(view.verdictAskLine);
  lines.push(view?.spineLine ?? "Spine check: not computed yet — `pb quality spine-check`");
  if (view?.atCapLine) lines.push(view.atCapLine);
  return lines;
}

/**
 * The "already oriented, brief mode, no task" shortcut (orient.ts) skips the full
 * `chain.getOrientView` fetch on purpose — it's the mid-session re-orientation shape, and paying
 * getOrientView's governance/domain/gather cost would defeat the point of a shortcut (T15). It
 * still owes the rituals surface, via the lightweight, dedicated `chain.getRitualsSurface` route
 * (registered convex/http.ts, security-contract-checked in
 * scripts/lib/directionTraceSecurityChecks.mjs) instead.
 */
export async function buildAlreadyOrientedResult(
  kernelQuery: <T>(fn: string, args?: Record<string, unknown>) => Promise<T>,
  agentSessionId: string | null,
  taskGroundingWarningLines: string[],
): Promise<ToolResult> {
  let ritualsFetchFailed = false;
  let rituals: RitualsSurfacePayload | null = null;
  try {
    // PR #532 late review fix (P2, "add a time bucket to the direct rituals query"): matches
    // conflictQueue.ts's minute-bucket convention — forces a fresh reactive result once a snooze
    // expires on wall-clock time alone, with no corresponding DB write to bust the cache.
    rituals = await kernelQuery<GatewayRouteReturnByName<"chain.getRitualsSurface">>("chain.getRitualsSurface", {
      now: Math.floor(Date.now() / 60_000) * 60_000,
    });
  } catch {
    ritualsFetchFailed = true;
  }
  const ritualsLines = buildRitualsSurfaceLines(rituals, { fetchFailed: ritualsFetchFailed });
  return {
    content: [{
      type: "text" as const,
      text:
        "Already oriented. Session active, writes unlocked.\n" +
        ritualsLines.join("\n") + "\n\n" +
        taskGroundingWarningLines.join("\n") +
        "Use `orient mode='full'` for workspace context or `orient task='...'` before substantive work.",
    }],
    structuredContent: success(
      "Already oriented. Session active, writes unlocked.",
      {
        alreadyOriented: true,
        sessionId: agentSessionId,
        taskGroundingStatus: "workspace_only",
        taskGroundingRequired: true,
        nextStep: 'Run `orient task="describe the work"` before substantive work.',
        // T1's undefined-vs-null distinction: omit the key on a fetch failure, keep an explicit
        // null for a genuine no-row arm.
        ...(rituals?.directionLine !== undefined ? { directionLine: rituals.directionLine } : {}),
        ...(rituals?.verdictAskLine !== undefined ? { verdictAskLine: rituals.verdictAskLine } : {}),
        ...(rituals?.spineLine !== undefined ? { spineLine: rituals.spineLine } : {}),
        // PR #538 late review fix (P2, "include at-cap disclosure in structured orient output"):
        // atCapLine renders in TEXT (buildRitualsSurfaceLines) but was never mirrored into
        // structuredContent — a structured-content consumer silently missed the dropped-at-cap
        // warning even when the same response's text carried it.
        ...(rituals?.atCapLine !== undefined ? { atCapLine: rituals.atCapLine } : {}),
      },
    ),
  };
}
